import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Check, CreditCard, Ticket } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHero, EmptyState, Reveal, PhotoSlot } from "@/components/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Evenements() {
  const [events, setEvents] = useState(null);
  const [buying, setBuying] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api
      .get("/events")
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]));
  }, []);

  const buy = async (e) => {
    e.preventDefault();
    if (!buying?.name || !buying?.email) {
      toast.error("Merci d'indiquer votre nom et votre email.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/events/${buying.event.id}/tickets`, {
        name: buying.name,
        email: buying.email,
        phone: buying.phone || "",
        quantity: parseInt(buying.quantity, 10),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSending(false);
    }
  };

  return (
    <div data-testid="evenements-page">
      <PageHero overline="Événements" title="Les rendez-vous de la maison" testId="evenements-hero" />

      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-6xl">
          {events === null ? null : events.length === 0 ? (
            <EmptyState text="Les prochains événements seront annoncés ici." />
          ) : (
            <div className="grid gap-12 md:grid-cols-2" data-testid="events-list">
              {events.map((event, i) => (
                <Reveal key={event.id} delay={i * 0.1}>
                  <article data-testid={`event-${event.id}`}>
                    <PhotoSlot src={event.photo} alt={event.title} ratio="aspect-[3/2]" />
                    {event.date && (
                      <p className="mt-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-copper">
                        <CalendarDays className="h-4 w-4" strokeWidth={1.5} />
                        {event.date}
                      </p>
                    )}
                    <h2 className="mt-3 text-2xl font-semibold tracking-tight text-ivory">
                      {event.title}
                    </h2>
                    {event.description && (
                      <p className="mt-3 max-w-lg text-sm leading-relaxed text-ivory/55">
                        {event.description}
                      </p>
                    )}

                    {event.price > 0 && (
                      <div className="mt-6" data-testid={`ticketing-${event.id}`}>
                        {buying?.event?.id === event.id ? (
                          <form
                            onSubmit={buy}
                            className="max-w-md space-y-4 rounded-lg border border-white/10 bg-coal/60 p-6"
                            data-testid={`ticket-form-${event.id}`}
                          >
                            <p className="flex items-center gap-2 text-sm font-semibold text-ivory">
                              <Ticket className="h-4 w-4 text-gold" strokeWidth={1.5} />
                              {event.price.toFixed(2).replace(".", ",")} € par place
                            </p>
                            <div className="grid gap-4 sm:grid-cols-2">
                              <input
                                value={buying.name}
                                onChange={(e) => setBuying((b) => ({ ...b, name: e.target.value }))}
                                placeholder="Nom et prénom"
                                className="input-dark !h-11"
                                data-testid={`ticket-name-${event.id}`}
                              />
                              <input
                                type="email"
                                value={buying.email}
                                onChange={(e) => setBuying((b) => ({ ...b, email: e.target.value }))}
                                placeholder="Email"
                                className="input-dark !h-11"
                                data-testid={`ticket-email-${event.id}`}
                              />
                            </div>
                            <div className="grid gap-4 sm:grid-cols-2">
                              <input
                                value={buying.phone}
                                onChange={(e) => setBuying((b) => ({ ...b, phone: e.target.value }))}
                                placeholder="Téléphone (optionnel)"
                                className="input-dark !h-11"
                                data-testid={`ticket-phone-${event.id}`}
                              />
                              <Select
                                value={buying.quantity}
                                onValueChange={(v) => setBuying((b) => ({ ...b, quantity: v }))}
                              >
                                <SelectTrigger
                                  className="!h-11 border-white/10 bg-white/5 text-sm text-ivory"
                                  data-testid={`ticket-qty-${event.id}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="border-white/10 bg-coal text-ivory">
                                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                                    <SelectItem key={n} value={String(n)}>
                                      {n} place{n > 1 ? "s" : ""}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex gap-3">
                              <button
                                type="button"
                                onClick={() => setBuying(null)}
                                className="btn-outline flex-1 !h-11 !text-[11px]"
                                data-testid={`ticket-cancel-${event.id}`}
                              >
                                Annuler
                              </button>
                              <button
                                type="submit"
                                disabled={sending}
                                className="btn-primary flex-1 !h-11 !text-[11px]"
                                data-testid={`ticket-pay-${event.id}`}
                              >
                                <CreditCard className="h-4 w-4" strokeWidth={1.5} />
                                {sending
                                  ? "Redirection…"
                                  : `Payer — ${(event.price * parseInt(buying.quantity, 10)).toFixed(2).replace(".", ",")} €`}
                              </button>
                            </div>
                          </form>
                        ) : (
                          <button
                            onClick={() =>
                              setBuying({ event, name: "", email: "", phone: "", quantity: "1" })
                            }
                            className="btn-gold"
                            data-testid={`ticket-buy-${event.id}`}
                          >
                            <Ticket className="h-4 w-4" strokeWidth={1.5} />
                            Réserver des places — {event.price.toFixed(2).replace(".", ",")} €
                          </button>
                        )}
                      </div>
                    )}
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function TicketSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/events/tickets/payment-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      attempts += 1;
      if (attempts < 10) {
        timer = setTimeout(poll, 2000);
      } else {
        setState("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="ticket-success-page">
      <PageHero
        overline="Événements"
        title={state === "paid" ? "Places réservées" : "Confirmation en cours"}
        testId="evenements-hero"
      />
      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-2xl">
          <Reveal>
            <div className="rounded-lg border border-gold/30 bg-coal/60 p-12 text-center">
              {state === "loading" && (
                <p className="text-sm text-ivory/60" data-testid="ticket-payment-loading">
                  Vérification du paiement…
                </p>
              )}
              {state === "paid" && (
                <>
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/40">
                    <Check className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  </span>
                  <h2 className="mt-8 text-2xl font-semibold tracking-tight text-ivory">Merci !</h2>
                  <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-ivory/55" data-testid="ticket-payment-confirmed">
                    Vos places sont réservées et réglées. Un email de confirmation avec votre
                    référence vient de vous être envoyé — présentez-le à l'entrée.
                  </p>
                </>
              )}
              {state === "pending" && (
                <p className="text-sm text-ivory/60" data-testid="ticket-payment-pending">
                  Paiement en cours de traitement. Vous recevrez un email de confirmation.
                </p>
              )}
              {state === "error" && (
                <p className="text-sm text-red-400" data-testid="ticket-payment-error">
                  Impossible de vérifier le paiement. Merci de nous contacter sur WhatsApp.
                </p>
              )}
              <Link to="/evenements" className="btn-outline mt-10" data-testid="ticket-success-back">
                Retour aux événements
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
